import { useMemo } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from '@/app/Toast';
import { buildRoutes, PROVIDER_FUTURE, ROUTER_FUTURE } from '@/app/router';
import { RouteError } from '@/app/RouteError';
import { HOME_PATH } from '@/app/Forbidden';
import { BASE_PATH } from '@/lib/env';
import { isApiError } from '@/lib/api';
import { handlePeriodClosedMutationError } from '@/features/accounts/close/locked-store';

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    // BRIEF rule 8: ANY mutation refused with 409 `period_closed` opens the one
    // global «فترة مقفلة» dialog (rendered by <LockedPeriodHost/> in AppShell).
    // A mutation opts out with `meta: { periodClosed: 'manual' }`.
    mutationCache: new MutationCache({ onError: handlePeriodClosedMutationError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Never retry a refusal (4xx); one retry for network/5xx.
        retry: (count, err) => !(isApiError(err) && err.status >= 400 && err.status < 500) && count < 1,
      },
      // Writes are never retried automatically (409 rule).
      mutations: { retry: false },
    },
  });
}

export default function App() {
  const queryClient = useMemo(makeQueryClient, []);
  // A pathless root with the error page: a lazy chunk that vanished after a
  // deploy reloads once (RouteError), everything else gets the Arabic page.
  const router = useMemo(
    () => createBrowserRouter([{ errorElement: <RouteError homePath={HOME_PATH} />, children: buildRoutes() }], { basename: BASE_PATH, future: ROUTER_FUTURE }),
    [],
  );
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} future={PROVIDER_FUTURE} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
